import React from 'react';

function parseInline(text) {
  if (!text) return '';
  
  // Matches inline code, bold, italics, and link patterns
  const tokenRegex = /(`[^`]+`|\*\*[^*]+\*\*|__[^_]+__|[*][^*]+[*]|_[^_]+_|\[[^\]]+\]\([^)]+\))/g;
  const parts = text.split(tokenRegex);
  
  return parts.map((part, index) => {
    if (!part) return null;
    
    // Inline code
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={index} style={{
          background: 'var(--surface2)',
          padding: '2px 6px',
          borderRadius: 4,
          fontFamily: 'monospace',
          fontSize: '0.9em',
          color: 'var(--accent)',
          border: '1px solid var(--border)'
        }}>
          {part.slice(1, -1)}
        </code>
      );
    }
    
    // Bold
    if ((part.startsWith('**') && part.endsWith('**')) || (part.startsWith('__') && part.endsWith('__'))) {
      return <strong key={index} style={{ fontWeight: 700 }}>{part.slice(2, -2)}</strong>;
    }
    
    // Italics
    if ((part.startsWith('*') && part.endsWith('*')) || (part.startsWith('_') && part.endsWith('_'))) {
      return <em key={index} style={{ fontStyle: 'italic' }}>{part.slice(1, -1)}</em>;
    }
    
    // Link
    if (part.startsWith('[') && part.includes('](') && part.endsWith(')')) {
      const mid = part.indexOf('](');
      const linkText = part.slice(1, mid);
      const linkUrl = part.slice(mid + 2, -1);
      return (
        <a key={index} href={linkUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'underline', fontWeight: 600 }}>
          {linkText}
        </a>
      );
    }
    
    return part;
  });
}

export default function MarkdownRenderer({ text, content }) {
  const rawText = text || content;
  if (!rawText) return null;
  const lines = rawText.split('\n');
  const elements = [];
  
  let inCodeBlock = false;
  let codeLines = [];
  let codeLang = '';
  
  let inList = false;
  let listType = ''; // 'ul' | 'ol'
  let listItems = [];
  
  const flushList = () => {
    if (inList) {
      const Tag = listType;
      const items = [...listItems];
      elements.push(
        <Tag key={`list-${elements.length}`} style={{ margin: '0 0 1em 24px', paddingLeft: 0, listStylePosition: 'outside' }}>
          {items.map((item, idx) => (
            <li key={idx} style={{ marginBottom: 4, color: 'var(--text2)' }}>
              {parseInline(item)}
            </li>
          ))}
        </Tag>
      );
      inList = false;
      listItems = [];
    }
  };
  
  const flushCodeBlock = () => {
    if (inCodeBlock) {
      elements.push(
        <pre key={`code-${elements.length}`} style={{
          background: 'var(--surface2)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '12px 16px',
          overflowX: 'auto',
          fontFamily: 'monospace',
          fontSize: 13.5,
          lineHeight: 1.5,
          margin: '0 0 1em 0',
          color: 'var(--text)'
        }}>
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      inCodeBlock = false;
      codeLines = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Code blocks
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        flushCodeBlock();
      } else {
        flushList();
        inCodeBlock = true;
        codeLang = line.slice(3).trim();
      }
      continue;
    }
    
    if (inCodeBlock) {
      codeLines.push(line);
      continue;
    }
    
    // Headers
    const headerMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (headerMatch) {
      flushList();
      const level = headerMatch[1].length;
      const content = headerMatch[2];
      const HeaderTag = `h${level}`;
      
      const marginStyle = level === 1 
        ? { fontSize: '1.6em', margin: '0.8em 0 0.4em 0', fontWeight: 800 } 
        : level === 2 
        ? { fontSize: '1.3em', margin: '0.8em 0 0.4em 0', fontWeight: 700 }
        : { fontSize: '1.1em', margin: '0.8em 0 0.4em 0', fontWeight: 700 };

      elements.push(
        <HeaderTag key={`h-${elements.length}`} style={{ ...marginStyle, color: 'var(--text)', lineHeight: 1.3 }}>
          {parseInline(content)}
        </HeaderTag>
      );
      continue;
    }
    
    // Lists
    // Bullet list: *, -, +
    const ulMatch = line.match(/^[\*\-\+]\s+(.*)$/);
    if (ulMatch) {
      if (inList && listType !== 'ul') {
        flushList();
      }
      inList = true;
      listType = 'ul';
      listItems.push(ulMatch[1]);
      continue;
    }
    
    // Numbered list: 1., 2., etc.
    const olMatch = line.match(/^(\d+)\.\s+(.*)$/);
    if (olMatch) {
      if (inList && listType !== 'ol') {
        flushList();
      }
      inList = true;
      listType = 'ol';
      listItems.push(olMatch[2]);
      continue;
    }
    
    // Empty lines
    if (line.trim() === '') {
      flushList();
      elements.push(<div key={`space-${elements.length}`} style={{ height: '0.6em' }} />);
      continue;
    }
    
    // Paragraphs / Normal text lines
    flushList();
    elements.push(
      <p key={`p-${elements.length}`} style={{ margin: '0 0 0.5em 0', lineHeight: 1.6 }}>
        {parseInline(line)}
      </p>
    );
  }
  
  flushList();
  flushCodeBlock();
  
  return <div style={{ wordBreak: 'break-word' }}>{elements}</div>;
}
